/**
 * ── THE FEATURED SECTION IS ONE THING, NOT FIVE (2026-09-18) ────────────────────
 * Jack, on a five-card listing with three different button sets: "ctas buttons should be
 * standardized and buttons 1 or 2 should affect all not just one."
 *
 * Every Featured card is its own Content Builder block, so until now every card answered
 * "one button or two" and "what do the buttons say" for itself. Requesters change one
 * card and the other four keep the block default — View Floorplan / Schedule a Tour next
 * to View Offer / Get an Offer in one email. This module gives the SECTION the answer:
 *
 *   · `echSectionSwap`   — One button ⇄ Two buttons moves every card in the section.
 *   · `echSectionLabels` — a button label picked on one card is written to every card
 *                          (and each card's paired URL re-derives, exactly as a single
 *                          card's change handler does today).
 *   · `echCarryLabels`   — a card added to the section starts with the section's labels,
 *                          not the block's defaults.
 *
 * What stays per card: the plan, the photo, the stats, and a URL the requester typed by
 * hand (`echDynPatch` skips a taken-over field). Nothing here invents copy — a label is
 * carried only when the source card has one.
 */
import { ECH_CHOICES } from "./echChoice";
import { echSwapFor, echSwapTo } from "./echSwap";
import { echNameOf } from "./echRegistry";
import {
  echDynPatch, echPickPatch, ECH_PLAN_PICK, ECH_HOME_PICK, ECH_CARDS_AUTO, echFeatFieldUnion, echFeatOf,
  echPlanScope, echPlanCardIdx, echPlanDeal, echSoleDeal, echHomeCardFallback, echPacks, echPlanDry, type DynBlock, type EchCtx,
} from "./echDyn";
import { homeKey, type Pack } from "./dynamicFill";

export interface SectionCard { id: string; type: string; data: Record<string, unknown> }

/** true when `type` is `blockType` itself or its One-button / Two-buttons sibling */
export function echSameSection(blockType: string, type: string): boolean {
  if (!type) return false;
  if (type === blockType) return true;
  const sw = echSwapFor(echNameOf(blockType));
  if (!sw) return false;
  const other = echNameOf(type);
  return !!other && sw.opts.some(([n]) => n === other);
}

/** the button-label keys a block declares as approved-label dropdowns (`BtnLabel`, `btn1Label`…) */
export function echCtaLabelKeys(name: string): string[] {
  const m = ECH_CHOICES[String(name || "")] || {};
  return Object.keys(m).filter((k) => m[k] === "cta");
}

/** a sibling block's key for `k` — the swap map when the two blocks differ, `k` itself otherwise */
function siblingKey(fromName: string, toName: string, k: string): string {
  if (fromName === toName) return k;
  const sw = echSwapFor(fromName);
  const map = (sw && sw.map[toName]) || {};
  return map[k] || k;
}

/**
 * "The requester chose this wording" (2026-09-25). Set when a label is picked on any card
 * and carried to every card of the section. While it is set, switching the section between
 * homes and floorplans leaves the wording alone; while it is not, the wording follows the
 * level ("View Home" / "View Floorplan"), see the level rule at the end of `echDynPatch`.
 */
export const ECH_LBL_SET = "__lblSet";

/** the label fields of `data` that carry text — what a new or swapped sibling inherits */
export function echCarryLabels(data: Record<string, unknown>, type: string): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  echCtaLabelKeys(echNameOf(type)).forEach((k) => {
    const v = data[k];
    if (v != null && String(v).trim() !== "") out[k] = v;
  });
  if (String(data[ECH_LBL_SET] ?? "") === "1") out[ECH_LBL_SET] = "1";
  return out;
}

/* ══════════════════════════════════════════════════════════════════════════════════
   A PICK REPLACES THE WHOLE SECTION, AND EACH LEVEL REMEMBERS ITS PICKS (2026-09-25)
   ══════════════════════════════════════════════════════════════════════════════════
   Jack, after the prod drive: "i do want to replace the whole section… but if they toggle
   back their plans or communities would be there to pick where they left off." Measured on
   prod: two home cards, card 2 selected, "Choose floorplans" → Marsala + Sierra left card 1
   as a home and produced three cards (one home, two plans) with three different buttons.

   The section's picks per level ride on every card as `__secPlans` / `__secHomes` (one
   stored pick per line, the same strings `__planPick` / `__homePick` hold), so switching
   Floorplans → Homesites → Floorplans in the picker opens on the floorplans chosen before.
   The current level's truth is always the cards themselves; the memory only answers for
   the level the section is not on. */
export const ECH_SEC_PLANS = "__secPlans";
export const ECH_SEC_HOMES = "__secHomes";

const lines = (v: unknown): string[] => String(v ?? "").split("\n").map((x) => x.trim()).filter(Boolean);
const uniq = (a: string[]): string[] => a.filter((x, i) => a.indexOf(x) === i);

/** the section's picks for each level: the cards' own when the section is on that level, else the memory */
export function echSectionMemory(cards: SectionCard[], level: string): { plans: string[]; homes: string[] } {
  const own = (k: string) => uniq(cards.map((c) => lines(c.data[k])[0] || "").filter(Boolean));
  const mem = (k: string) => {
    for (const c of cards) { const l = lines(c.data[k]); if (l.length) return uniq(l); }
    return [] as string[];
  };
  const plansOwn = own(ECH_PLAN_PICK);
  const homesOwn = own(ECH_HOME_PICK);
  return {
    /* the section's CURRENT level answers from its cards only (2026-10-27, Layne: "once,
       picking one plan added about five cards, none of them the one I picked"). The saved
       list used to stand in when no card held a pick of its own, so old picks came back
       pre-ticked, crowded the cap of five and were rebuilt instead of the new pick. The
       memory is for the level the section is NOT on, which is what it was written for. */
    plans: level === "plans" ? plansOwn : (mem(ECH_SEC_PLANS).length ? mem(ECH_SEC_PLANS) : plansOwn),
    homes: level === "homes" ? homesOwn : (mem(ECH_SEC_HOMES).length ? mem(ECH_SEC_HOMES) : homesOwn),
  };
}

/** the level the SECTION is on: plans if any card is a floorplan card, else homes if any is a
 *  home card, else "" (community cards only). The selected card's own level is not the
 *  section's: a card just added from "Add card" starts at community level next to plan cards
 *  (2026-10-27, from the review of Layne's fix). */
export function echSectionLevel(cards: SectionCard[]): "plans" | "homes" | "" {
  let homes = false;
  for (const c of cards) {
    const f = echFeatOf(c as DynBlock);
    if (f === "plans") return "plans";
    if (f === "homes") homes = true;
  }
  return homes ? "homes" : "";
}

/** what a fresh card cloned from the section's first card must not inherit */
const CLONE_DROP = [ECH_PLAN_PICK, ECH_HOME_PICK, "__socOff", "__layStash", "__cardsAuto", "__lots", "PlanImage__nat"];

/**
 * The section as the pick says it should be: one card per pick, in pick order, all on the
 * first card's block (One or Two buttons) and all carrying the first card's labels. Card N
 * reuses the section's N-th existing card (its id, so the selection and the SFMC re-pull key
 * hold); picks past the existing cards clone card 1 without its pick, claims or stash.
 * Returns `null` when there is nothing to replace with (no picks), so the caller keeps the
 * old single-card path.
 */
export function echReplaceSection(o: {
  cards: SectionCard[];
  level: string;
  want: "plan" | "home";
  keys: string[];
  ctx: EchCtx;
  doc: DynBlock[];
  /** fields to blank on a card whose level is changing (the Inspector's level-change set) */
  stale: (c: SectionCard) => Record<string, unknown>;
}): SectionCard[] | null {
  const keys = o.keys.filter(Boolean);
  if (!keys.length || !o.cards.length) return null;
  /* every card is read as what it SHOWS: an unpicked card is pinned to its positional plan or
     home first, so it matches the pick the picker ticked for it and keeps its typed edits
     (2026-10-27, review of Layne's fix) */
  const cards = echPinShown(o.cards, o.doc, o.ctx);
  const first = cards[0];
  const type = first.type;
  const labels = echCarryLabels(first.data, type);
  /* the memory of the level the section is on NOW comes from its cards; the other level's
     from what was saved */
  const mem = echSectionMemory(cards, echSectionLevel(cards) || o.level);
  if (o.want === "plan") mem.plans = keys; else mem.homes = keys;
  const pickKey = o.want === "plan" ? ECH_PLAN_PICK : ECH_HOME_PICK;
  const pickOf = (c: SectionCard) => String(c.data[pickKey] || "").split("\n")[0].trim();
  /* ── A PICKED PLAN KEEPS ITS OWN CARD (2026-10-27) ──────────────────────────────────
     Card N used to be reused for pick N, so unticking the first of three plans moved every
     plan onto its neighbour's card and each card's typed edits onto the wrong plan. A key a
     card already holds goes back to that card; the rest take the unused cards in order. */
  const used = new Set<number>();
  const srcIdx = keys.map((key) => {
    const j = cards.findIndex((c, k) => !used.has(k) && pickOf(c) === key);
    if (j > -1) used.add(j);
    return j;
  });
  srcIdx.forEach((j, i) => {
    if (j > -1) return;
    const k = cards.findIndex((_, n) => !used.has(n));
    if (k > -1) { used.add(k); srcIdx[i] = k; }
  });
  const out: SectionCard[] = [];
  keys.forEach((key, i) => {
    const src = srcIdx[i] > -1 ? cards[srcIdx[i]] : undefined;
    let base: Record<string, unknown>;
    let id: string;
    if (src && src.type === type) { base = { ...src.data }; id = src.id; }
    else if (src) {
      /* a stray One/Two-button mismatch is brought onto the section's block */
      base = { ...first.data }; CLONE_DROP.forEach((k) => { delete base[k]; }); id = src.id;
    } else { base = { ...first.data }; CLONE_DROP.forEach((k) => { delete base[k]; }); id = ""; }
    const card: SectionCard = { id: id || "__new" + i, type, data: base };
    const data0: Record<string, unknown> = { ...base, ...o.stale(card), ...labels, __echMode: "dynamic" };
    /* a reused card whose pick CHANGED gives up its claims on the card's own fields, so the new
       pick shows rather than the old card's typed name and specs (2026-10-27, Layne) — the
       same rule `echPickPatch` applies to `plan.self.*` since 2026-09-15 ("picking a different
       plan makes the card that plan, typed name included"), widened to every field a level
       feeds now that a blank card can claim them. */
    if (src && pickOf(src) !== key) {
      const fed = echFeatFieldUnion(echNameOf(type));
      data0.__socOff = String(data0.__socOff || "").split(",").filter(Boolean).filter((k) => !fed[k]).join(",");
    }
    let pk: Record<string, string> = {};
    try {
      pk = echPickPatch({ id: card.id, type, data: data0 }, o.ctx, o.doc, {
        featLevel: o.level,
        planPick: o.want === "plan" ? key : undefined,
        homePick: o.want === "home" ? key : undefined,
      });
    } catch { pk = { __featLevel: o.level, [o.want === "plan" ? ECH_PLAN_PICK : ECH_HOME_PICK]: key }; }
    /* `__cardsAuto`: the requester decided this set, so the fill does not grow it back to three
       or five with plans nobody picked (2026-10-27, Layne: "adds more than I picked") */
    out.push({ id, type, data: { ...data0, ...pk, [ECH_SEC_PLANS]: mem.plans.join("\n"), [ECH_SEC_HOMES]: mem.homes.join("\n"), [ECH_CARDS_AUTO]: "1" } });
  });
  /* the parked Listing Table (table → cards) lives on one card; if that card is dropped the
     stash moves to the first card, or the next "Listing table" toggle would rebuild blank */
  const stashed = cards.find((c) => c.data.__layStash);
  if (stashed && !out.some((c) => c.data.__layStash)) out[0].data.__layStash = stashed.data.__layStash;
  return out;
}

/**
 * Write the labels of `from` onto every OTHER card and re-derive each card's paired
 * URL. Returns only the cards that change. `ctx` may be null (no feed loaded) — the
 * labels still travel, the URLs are left as they are.
 */
export function echSectionLabels(cards: SectionCard[], from: SectionCard, ctx: EchCtx | null, doc: DynBlock[]): SectionCard[] {
  const fromName = echNameOf(from.type);
  const keys = echCtaLabelKeys(fromName);
  if (!keys.length) return [];
  const out: SectionCard[] = [];
  for (const c of cards) {
    if (c.id === from.id) continue;
    const name = echNameOf(c.type);
    const mine = new Set(echCtaLabelKeys(name));
    const patch: Record<string, unknown> = {};
    keys.forEach((k) => {
      const v = from.data[k];
      if (v == null || String(v).trim() === "") return;
      const tk = siblingKey(fromName, name, k);
      if (!mine.has(tk)) return;
      if (String(c.data[tk] ?? "") === String(v)) return;
      patch[tk] = v;
    });
    if (String(from.data[ECH_LBL_SET] ?? "") === "1" && String(c.data[ECH_LBL_SET] ?? "") !== "1") patch[ECH_LBL_SET] = "1";
    if (!Object.keys(patch).length) continue;
    const data = { ...c.data, ...patch };
    if (ctx) {
      try { Object.assign(data, echDynPatch({ id: c.id, type: c.type, data }, name, ctx, doc)); }
      catch { /* a resolver miss must not lose the label */ }
    }
    out.push({ id: c.id, type: c.type, data });
  }
  return out;
}

/**
 * Swap EVERY card in the section to `toName`, then give the whole section the selected
 * card's labels so the swap lands uniform. A card already on `toName` keeps its data.
 * Returns null when the sibling block is not registered yet — the caller shows the toast.
 */
export function echSectionSwap(cards: SectionCard[], selfId: string, toName: string, ctx: EchCtx | null, doc: DynBlock[]): SectionCard[] | null {
  const swapped: SectionCard[] = [];
  for (const c of cards) {
    const from = echNameOf(c.type);
    if (from === toName) { swapped.push({ id: c.id, type: c.type, data: { ...c.data } }); continue; }
    const r = echSwapTo(c as DynBlock, from, toName);
    if (!r) return null;
    swapped.push({ id: c.id, type: r.type, data: r.data });
  }
  const self = swapped.find((c) => c.id === selfId);
  if (!self) return swapped;
  const changed = new Map(echSectionLabels(swapped, self, ctx, doc).map((c) => [c.id, c]));
  return swapped.map((c) => changed.get(c.id) || c);
}

/* ══════════════════════════════════════════════════════════════════════════════════
   A CARD WITHOUT A PICK IS PINNED TO WHAT IT SHOWS (2026-10-27, Layne, Teams)
   ══════════════════════════════════════════════════════════════════════════════════
   "Deleting a card removes the wrong one. With several Featured cards, clicking the X on any
   card deletes the last card, not the one I clicked." And: "Adding a plan or home from
   inventory sometimes removes what's already there."

   A Featured card with no stored pick shows a plan (or home) by its POSITION among the cards.
   So removing card 2 really did remove card 2, but card 3 then sat in position 2 and showed
   card 2's plan, and so on down: the email lost its LAST plan. And the picker opened with
   only the stored picks ticked, so the cards showing a positional plan were not ticked and a
   pick replaced them. Pinning each card to the plan it is showing right now makes both
   behave as they look: delete removes the card you clicked, and the picker opens with every
   card on screen ticked. */

/** the pick an unpicked card is showing by position, or null (picked already, community
 *  level, nothing resolvable, or a homesite-backed plan row with no plan of its own) */
export function echShownPick(cardId: string, doc: DynBlock[], ctx: EchCtx): Record<string, string> | null {
  const m = doc.find((b) => b.id === cardId);
  if (!m || !ctx || ctx.pending) return null;          /* nothing is shown for sure while packs load */
  const lvl = echFeatOf(m);
  const d = m.data || {};
  if (lvl === "plans") {
    if (String(d[ECH_PLAN_PICK] || "").trim()) return null;
    /* a card past what the scope can fill shows the template's placeholder, not a plan */
    if (echPlanDry(m, ctx, doc)) return null;
    const scope = echPlanScope(ctx);
    if (!scope.length) return null;
    const cIdx = echPlanCardIdx(doc, m);
    let C: Pack["C"] | undefined;
    let pIdx = 0;
    if (scope.length > 1) {
      const one = echPlanDeal(scope)[cIdx - 1];
      if (!one || !(one.C.plans || []).length) return null;
      C = one.C; pIdx = one.pIdx;
    } else {
      const one = echSoleDeal(ctx, scope)[cIdx - 1];
      if (!one || one.home) return null;
      C = one.C; pIdx = one.pIdx;
    }
    const pl = C && (C.plans || [])[pIdx - 1];
    if (!C || !pl) return null;
    /* the PACK's code, the one the picker (planPickKey) and the fill look packs up by */
    const pack = scope.concat(echPacks(ctx)).find((p) => p.C === C);
    const code = String((pack && pack.code) || "");
    const num = String(pl.num == null ? "" : pl.num).trim();
    const name = String(pl.name || "").trim();
    if (!code || (!num && !name)) return null;
    return { [ECH_PLAN_PICK]: code + "|pn|" + num + "|" + name };
  }
  if (lvl === "homes") {
    if (String(d[ECH_HOME_PICK] || "").trim()) return null;
    const f = echHomeCardFallback(doc, m, echPacks(ctx));
    if (!f) return null;
    return { [ECH_HOME_PICK]: homeKey(f.code, f.h) };
  }
  return null;
}

/**
 * The section's cards with every unpicked card pinned to what it shows, and the set marked as
 * decided (`__cardsAuto`) so the next fill does not grow it back. `doc` is the document as it
 * is NOW — positions are read before anything is removed.
 */
export function echPinShown(cards: SectionCard[], doc: DynBlock[], ctx: EchCtx): SectionCard[] {
  return cards.map((c) => {
    const pin = echShownPick(c.id, doc, ctx);
    return { id: c.id, type: c.type, data: { ...c.data, ...(pin || {}), [ECH_CARDS_AUTO]: "1" } };
  });
}

/**
 * ── ✕ REMOVES THE CARD YOU CLICKED (2026-10-27, Layne: "clicking the X on any card deletes the
 * last card, not the one I clicked") ─────────────────────────────────────────────────────────
 * What the Studio's delete must write onto the OTHER Featured cards at the deleted card's level
 * so each keeps showing what it shows now: `{ [cardId]: patch }`, or null for a plain delete.
 *
 * Read off the document BEFORE the card goes. Null (plain delete, as before) when:
 *  · the block is not a plans/homes Featured card, or there is no feed / packs are loading;
 *  · no unpicked card sits AFTER the deleted one, so nothing would move up a plan;
 *  · any unpicked card at that level cannot be pinned (a homesite-backed row, a card past the
 *    scope's ceiling): pinning some and not others re-deals the rest and duplicates a plan.
 * Every remaining card is also marked as a decided set (`__cardsAuto`), so the fill does not
 * grow the section back: a delete is permanent, which is what `echPlanCardsNeeded` promises.
 */
export function echPinOnDelete(doc: DynBlock[], id: string, ctx: EchCtx | null | undefined): Record<string, Record<string, string>> | null {
  if (!ctx || ctx.pending) return null;
  const gone = doc.find((b) => b.id === id);
  if (!gone || !/^ECH_Featured/.test(echNameOf(gone.type))) return null;
  const lvl = echFeatOf(gone);
  if (lvl !== "plans" && lvl !== "homes") return null;
  const pickKey = lvl === "plans" ? ECH_PLAN_PICK : ECH_HOME_PICK;
  const same = doc.filter((b) => /^ECH_Featured/.test(echNameOf(b.type)) && echFeatOf(b) === lvl);
  const at = same.indexOf(gone);
  const unpicked = (b: DynBlock) => !String((b.data && b.data[pickKey]) || "").trim();
  if (!same.slice(at + 1).some(unpicked)) return null;
  const out: Record<string, Record<string, string>> = {};
  for (const b of same) {
    if (b.id === id) continue;
    if (!unpicked(b)) { out[b.id] = { [ECH_CARDS_AUTO]: "1" }; continue; }
    const pin = echShownPick(b.id, doc, ctx);
    if (!pin) return null;
    out[b.id] = { ...pin, [ECH_CARDS_AUTO]: "1" };
  }
  return out;
}
